import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Sidebar from "../sidebar/sidebar";
import { Button } from "@/components/ui/button-tw";
import { ArrowLeft, Clock, LampDesk, Loader2 } from "lucide-react";
import { cn } from "@/utils/classnames";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "react-toastify";

import notebook from "@/assets/images/notebook-sweeps.png";
import { sweepQueries } from "@/queries/sweeps";
import { useAuth } from "@/providers/auth";
import { BsBank } from "react-icons/bs";
import { Checkbox } from "@/components/ui/check-box";
import { CheckedState } from "@radix-ui/react-checkbox";
import {
  Header,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  Selection,
  Separator,
} from "react-aria-components";

import { Label } from "@/components/ui/label";
import { usePlatformUsers } from "@/hooks/tenancy/use-platform-users";
import { useAuthUsers } from "@/hooks/tenancy/use-auth-users";
import { usePaymentMethods } from "@/queries/payment-method/use-payment-methods";

const sstUrl =
  "https://vault.pactsafe.io/s/baa76388-6efd-4b54-b2c2-ff6cd8ab2be9/legal#sweeps-terms";

export default function SweepsSetup() {
  const { cognitoId, clientId, userRole, email } = useAuth();
  const isAdmin = userRole === "admin";
  const userId =
    userRole === "admin" || userRole === "auth_user" ? clientId : cognitoId;

  const [currentWidth, setCurrentWidth] = useState(0);
  const history = useHistory();
  const [step, setStep] = useState(0);

  useEffect(() => {
    if (isAdmin) {
      toast.info(
        "Reach out to your account owner or authorized user to set up Sweeps."
      );
    }
  }, []);
  const paymentMethodsQuery = usePaymentMethods(userId);
  const [selectedBankAccount, setSelectedBankAccount] = useState<
    string | undefined
  >(undefined);
  const { mutate, isLoading } = sweepQueries.settings.create.useMutation();
  const adminsQuery = usePlatformUsers({ userType: "admin" });
  const usersQuery = useAuthUsers();
  const [sstAuthorization, setSstAuthorization] = useState<CheckedState>(false);
  const [selectedNotificationUsers, setSelectedNotificationUsers] = useState<Selection>(
    new Set()
  );
  const [wantToReceiveNotification, setWantToReceiveNotification] =
    useState(false);

  const getSelectedUsersEmails = () => {
    const selectedUsersEmails = Array.from(selectedNotificationUsers).map(
      (userId) => {
        const admin = adminsQuery.data.find(
          ({ user_id }) => user_id === userId
        );
        if (admin) return admin.email_address;

        const authUser = usersQuery.data.find(
          ({ user_id }) => user_id === userId
        );

        if (authUser) return authUser.email;
      }
    );

    if (wantToReceiveNotification) {
      selectedUsersEmails.push(email);
    }

    return selectedUsersEmails;
  };

  const pm = paymentMethodsQuery.data?.find(
    (pm) => pm.payment_method_id === selectedBankAccount
  );

  return (
    <div className="min-h-screen">
      <Sidebar currentWidth={currentWidth} setCurrentWidth={setCurrentWidth} />
      <div className="relative min-h-screen overflow-auto ml-[415px] bg-[#f8f8f8]">
        <div className="min-h-screen flex items-center">
          <div className="flex h-[800px] grow items-center max-w-[1538px]">
            <div
              className={cn(
                "bg-white p-8 border border-border rounded-xl shadow-sm h-2/3 -mr-24 z-10 w-[600px] 2xl:w-[800px] max-w-[800px] flex flex-col justify-between"
              )}
            >
              <div>
                <span
                  className={cn(
                    "hidden rounded-full bg-gray-800 text-white text-sm font-semibold py-1.5 px-3 mb-4",
                    {
                      "inline-block": step > 0,
                    }
                  )}
                >
                  Step {step} of 3
                </span>
                <h3 className="text-2xl 2xl:text-4xl font-semibold text-gray-900">
                  {step === 0
                    ? `Automatic Sweeps: Effortless Transfers, On Your Terms`
                    : step === 1
                    ? `Where Should We Send The Funds?`
                    : step === 2
                    ? `Who should receive Sweep Notifications?`
                    : `Review and Submit Your Automatic Sweep Setup`}
                </h3>
                {step === 0 ? (
                  <div className="mt-6">
                    <p className="font-semibold text-lg 2xl:text-xl">
                      Here's how it works:
                    </p>
                    <ul className="text-gray-500 text-sm xl:text-lg mt-4">
                      <li className="flex items-baseline gap-3 ">
                        <div className="rounded-full h-2.5 w-2.5 bg-gray-900 -translate-y-0.5"></div>
                        <span>
                          Payments you receive for your invoices will{" "}
                          <span className="font-semibold">
                            automatically transfer
                          </span>{" "}
                          3 business days after they clear.
                        </span>
                      </li>
                      <li className="flex items-baseline gap-3">
                        <div className="rounded-full h-2.5 w-2.5 bg-gray-900 -translate-y-0.5"></div>
                        <span>
                          Swept funds typically arrive in your{" "}
                          <span className="font-semibold">
                            designated linked account
                          </span>{" "}
                          within 1-3 days.
                        </span>
                      </li>
                      <li className="flex items-baseline gap-3">
                        <div className="rounded-full h-2.5 w-2.5 bg-gray-900 -translate-y-0.5"></div>
                        <span>
                          You’ll receive an{" "}
                          <span className="font-semibold">
                            email notification
                          </span>{" "}
                          every time a Sweep occurs – you choose the recipients.
                        </span>
                      </li>
                    </ul>
                  </div>
                ) : step === 1 ? (
                  <div className="mt-6">
                    <p className="font-semibold text-lg 2xl:text-xl mb-4">
                      Choose a linked account to receive your Sweep transfers.
                    </p>
                    {paymentMethodsQuery.data ? (
                      <Select
                        onValueChange={(value) => setSelectedBankAccount(value)}
                        defaultValue={selectedBankAccount}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Select bank account" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectGroup>
                            <SelectLabel>Bank Accounts</SelectLabel>
                            {paymentMethodsQuery.data.map(
                              ({ payment_method_data, payment_method_id, payment_method_nickname }) => (
                                <SelectItem
                                  key={payment_method_id}
                                  value={payment_method_id}
                                >{payment_method_nickname || payment_method_data.name_on_account}</SelectItem>
                              )
                            )}
                          </SelectGroup>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Button onClick={() => history.push("/move-money")}>
                        Link a payment method
                      </Button>
                    )}
                  </div>
                ) : step === 2 ? (
                  <div className="mt-6">
                    <div className="*:not-first:mt-2">
                      <div className="flex items-baseline gap-1 mb-1">
                        <Label>Choose Users</Label>
                        <span className="text-gray-400 text-sm font-medium">{`(Optional)`}</span>
                      </div>
                      <div className="border-input overflow-hidden rounded-md border">
                        <ListBox
                          className="bg-background max-h-48 min-h-20 space-y-2 overflow-auto p-1 text-sm shadow-xs transition-[color,box-shadow]"
                          aria-label="Select some foods"
                          selectionMode="multiple"
                          selectedKeys={selectedNotificationUsers}
                          onSelectionChange={setSelectedNotificationUsers}
                        >
                          <ListBoxSection className="space-y-1">
                            <Header className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                              Admins
                            </Header>
                            {adminsQuery.data?.map((adm) => (
                              <ListBoxItem
                                id={adm.user_id}
                                key={adm.user_id}
                                className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-focus-visible:border-ring data-focus-visible:ring-ring/50 relative rounded px-2 py-1.5 outline-none data-disabled:cursor-not-allowed data-disabled:opacity-50 data-focus-visible:ring-[3px]"
                              >
                                {adm.email_address}
                              </ListBoxItem>
                            ))}
                          </ListBoxSection>
                          <Separator className="bg-border -mx-1 my-2 h-px" />
                          <ListBoxSection className="space-y-1">
                            <Header className="text-muted-foreground px-2 py-1.5 text-xs font-medium">
                              Authorized Users
                            </Header>
                            {usersQuery.data?.map((user) => (
                              <ListBoxItem
                                id={user.user_id}
                                key={user.user_id}
                                className="data-[selected=true]:bg-accent data-[selected=true]:text-accent-foreground data-focus-visible:border-ring data-focus-visible:ring-ring/50 relative rounded px-2 py-1.5 outline-none data-disabled:cursor-not-allowed data-disabled:opacity-50 data-focus-visible:ring-[3px]"
                              >
                                {user.email}
                              </ListBoxItem>
                            ))}
                          </ListBoxSection>
                        </ListBox>
                      </div>
                      <p className="text-sm text-gray-900 mt-2">
                        Not seeing the person you want to add? Add them{" "}
                        <span
                          className="text-blue-600 hover:text-blue-600/75 hover:cursor-pointer hover:border-b border-blue-600"
                          onClick={() => history.push("/settings")}
                        >
                          here.
                        </span>
                      </p>
                      <div className="flex items-center gap-2 mt-4">
                        <Checkbox
                          checked={wantToReceiveNotification}
                          onCheckedChange={(checked) =>
                            setWantToReceiveNotification(
                              checked === "indeterminate" ? false : checked
                            )
                          }
                        />
                        <Label>
                          I'd like to receive notifications that a Sweep has
                          occurred
                        </Label>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-6 flex flex-col gap-2 justify-between">
                    <div>
                      <div className="group relative flex items-start space-x-3 py-6 border-y ">
                        <div className="flex-shrink-0">
                          <span
                            className={cn(
                              "bg-slate-500 inline-flex h-12 w-12 items-center justify-center rounded-lg",
                              {
                                "bg-yellow-500":
                                  pm?.payment_method_status ===
                                    "pending_manual_verification" ||
                                  pm?.payment_method_status ===
                                    "pending_disconnect",
                              }
                            )}
                          >
                            <BsBank
                              aria-hidden="true"
                              className="h-8 w-8 text-white"
                            />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className=" font-medium text-gray-900">
                            {`${pm?.payment_method_data.name_on_account}`}
                          </div>
                          <p className=" text-gray-500">
                            {pm?.payment_method_data.ach?.account_number
                              ? `***${pm?.payment_method_data.ach?.account_number}`
                              : "********"}
                            {pm?.payment_method_status ===
                              "pending_disconnect" && (
                              <span className="text-red-600 ml-3">
                                Expiring soon.{" "}
                                <span
                                  className="border-b border-b-red-600 hover:text-red-700 hover:cursor-pointer"
                                  onClick={() =>
                                    history.push(
                                      "/move-money?tab=payment-methods"
                                    )
                                  }
                                >
                                  Click here to verify
                                </span>
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="flex-shrink-0 self-center"></div>
                      </div>
                    </div>

                    <div className="items-top flex space-x-2 mt-6">
                      <Checkbox
                        checked={sstAuthorization}
                        onCheckedChange={setSstAuthorization}
                      />
                      <div className="grid gap-1.5 leading-none">
                        <p className="text-sm text-muted-foreground">
                          By checking this box, you acknowledge that you have
                          reviewed and agree to the{" "}
                          <a
                            href={sstUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="font-semibold underline"
                          >
                            Sweeps Service-Specific Terms
                          </a>
                          , and you authorize Pocketbook to initiate and manage
                          Sweep transfers on your behalf.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="flex gap-4 pt-8">
                <Button
                  className="min-w-[170px]"
                  variant="secondary"
                  onClick={() => {
                    if (step === 0) {
                      return history.push("/move-money?tab=sweeps");
                    }

                    setStep((step) => step - 1);
                  }}
                >
                  {step === 0 && <ArrowLeft className="h-4 w-4 mr-2" />}{" "}
                  {step === 0
                    ? `Back to
                  Move Money`
                    : `Back`}
                </Button>
                <Button
                  className="min-w-[170px]"
                  disabled={
                    isLoading ||
                    (step === 0 && isAdmin) ||
                    (step === 1 && !selectedBankAccount) ||
                    (step === 3 && !sstAuthorization)
                  }
                  onClick={() => {
                    if (step === 3) {
                      if (selectedBankAccount) {
                        const authorizationLang = `[] By checking this box, you acknowledge that you have reviewed and agree to the [${encodeURIComponent(
                          sstUrl
                        )}](Sweeps Service-Specific Terms), and you authorize Pocketbook to initiate and manage Sweep transfers on your behalf.`;

                        mutate(
                          {
                            userId,
                            payload: {
                              linked_account_id: selectedBankAccount,
                              authorization: authorizationLang,
                              emails: getSelectedUsersEmails(),
                            },
                          },
                          {
                            onSuccess: () => {
                              toast.success(
                                "Your sweep has been established successfully."
                              );
                              return history.push("/move-money?tab=sweeps");
                            },
                          }
                        );
                      }
                    } else {
                      setStep((step) => step + 1);
                    }
                  }}
                >
                  {step === 0
                    ? `Set Up Now`
                    : step === 1 || step === 2
                    ? `Next`
                    : `Finish`}
                  {isLoading && <Loader2 className="h4 w-4 ml-2" />}
                </Button>
              </div>
            </div>
            <div
              className={cn(
                "bg-skin-primary w-96 h-full grow p-6 pr-0 border border-border rounded-l-xl 2xl:rounded-r-xl shadow-sm pl-32 flex items-center"
              )}
            >
              {step === 0 ? (
                <div className="flex flex-col gap-12">
                  <div className="relative max-w-[470px]">
                    <div className="absolute rounded-md bg-white p-3">
                      <Clock aria-hidden="true" className="size-6 text-black" />
                    </div>
                    <div className="ml-16 mr-4">
                      <p className="truncate text-lg 2xl:text-xl font-medium text-white">
                        Save Time
                      </p>
                      <p className="text-base 2xl:text-lg  text-gray-100">
                        Automate cash flow so AR associates spend less time
                        chasing payments without surprises.
                      </p>
                    </div>
                  </div>
                  <div className="relative max-w-[470px]">
                    <div className="absolute rounded-md bg-white p-3">
                      <LampDesk
                        aria-hidden="true"
                        className="size-6 text-black"
                      />
                    </div>
                    <div className="ml-16 mr-4">
                      <p className=" truncate text-lg 2xl:text-xl font-medium text-white">
                        Gain Visibility
                      </p>
                      <p className="text-base 2xl:text-lg text-gray-100">
                        No more scrambling to move funds at the last minute.
                        Sweeps helps you have complete oversight into every
                        dollar moving through your accounts.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  style={{ backgroundImage: `url(${notebook})` }}
                  className={`bg-cover bg-center lg:h-[490px] 2xl:h-[599px] w-full z-[1]`}
                ></div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
